import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Sliders,
  Percent,
  Search,
  RotateCcw,
  Download,
  Upload,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Equal,
  Sparkles,
  Pencil,
  Check,
  X,
  FileSpreadsheet,
  Package,
  Layers,
} from 'lucide-react';
import {
  fetchPriceAdjustmentBrands,
  fetchPriceAdjustmentProducts,
  updatePurchasePrice,
  applyPriceAdjustments,
  parseSupplierPriceFile,
  commitSupplierMatches,
  searchProductsForMatching,
  type ParsedSupplierRowItem,
  type MatchedProductItem,
} from '../api.js';
import type { PriceRoundingStrategy, PriceAdjustmentStatus, PriceAdjustmentProductItem } from '@mapara/core';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Panel } from '../ui/panels.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { cn } from '../ui/cn.js';
import { formatMillimes } from './CompetitorPricesPage.js';
import { SubBrandPills } from '../components/SubBrandPills.js';

export function PriceAdjustmentPage(): React.ReactElement {
  const queryClient = useQueryClient();

  // Top control filters
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [excludedSubBrands, setExcludedSubBrands] = useState<string[]>([]);
  const [brandSearchInput, setBrandSearchInput] = useState<string>('');
  const [marginPct, setMarginPct] = useState<number>(25);
  const [rounding, setRounding] = useState<PriceRoundingStrategy>('ten_millimes');
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<PriceAdjustmentStatus | 'all' | 'need_update'>('all');
  const [page, setPage] = useState<number>(1);

  // Row selection for batch action
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [syncToWoo, setSyncToWoo] = useState<boolean>(true);

  // Inline purchase price editing
  const [editingProductId, setEditingProductId] = useState<number | null>(null);
  const [inlinePriceInput, setInlinePriceInput] = useState<string>('');

  // Supplier file matcher modal state
  const [matcherModalOpen, setMatcherModalOpen] = useState<boolean>(false);
  const [pastedText, setPastedText] = useState<string>('');
  const [parsedRows, setParsedRows] = useState<ParsedSupplierRowItem[]>([]);
  const [matcherSupplierName, setMatcherSupplierName] = useState<string>('Fournisseur Grossiste');
  const [activeTab, setActiveTab] = useState<'upload' | 'review'>('upload');
  const [searchModalRowId, setSearchModalRowId] = useState<string | null>(null);
  const [productSearchQuery, setProductSearchQuery] = useState<string>('');
  const [excludedRowIds, setExcludedRowIds] = useState<Set<string>>(new Set());

  // 1. Fetch brands
  const brandsQuery = useQuery({
    queryKey: ['price-adjustment-brands'],
    queryFn: fetchPriceAdjustmentBrands,
  });

  // Filtered brands for quick searching in dropdown
  const filteredBrands = useMemo(() => {
    const list = brandsQuery.data?.brands ?? [];
    if (!brandSearchInput.trim()) return list;
    const q = brandSearchInput.toLowerCase().trim();
    return list.filter((b) => b.brand.toLowerCase().includes(q));
  }, [brandsQuery.data?.brands, brandSearchInput]);

  // 2. Fetch products
  const productsQuery = useQuery({
    queryKey: ['price-adjustment-products', selectedBrand, excludedSubBrands, search, marginPct, rounding, statusFilter, page],
    queryFn: () =>
      fetchPriceAdjustmentProducts({
        ...(selectedBrand !== 'all' ? { brand: selectedBrand } : {}),
        ...(excludedSubBrands.length > 0 ? { excludedSubBrands: excludedSubBrands.join(',') } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
        marginPct,
        rounding,
        statusFilter,
        page,
        limit: 100,
      }),
  });

  const products = productsQuery.data?.items ?? [];
  const summary = productsQuery.data?.summary;
  const pagination = productsQuery.data?.pagination;

  // Inline price update mutation
  const updatePriceMutation = useMutation({
    mutationFn: updatePurchasePrice,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['price-adjustment-products'] });
      void queryClient.invalidateQueries({ queryKey: ['price-adjustment-brands'] });
      setEditingProductId(null);
    },
  });

  // Batch apply mutation
  const applyMutation = useMutation({
    mutationFn: applyPriceAdjustments,
    onSuccess: (data) => {
      alert(data.message);
      setSelectedIds(new Set());
      void queryClient.invalidateQueries({ queryKey: ['price-adjustment-products'] });
    },
    onError: (err) => {
      alert(`Erreur d'application: ${err instanceof Error ? err.message : String(err)}`);
    },
  });

  // Parse supplier file mutation
  const parseFileMutation = useMutation({
    mutationFn: parseSupplierPriceFile,
    onSuccess: (data) => {
      setParsedRows([...data.rows]);
      setActiveTab('review');
    },
    onError: (err) => {
      alert(`Erreur d'analyse: ${err instanceof Error ? err.message : String(err)}`);
    },
  });

  // Commit matches mutation
  const commitMatchesMutation = useMutation({
    mutationFn: commitSupplierMatches,
    onSuccess: (data) => {
      alert(data.message);
      setMatcherModalOpen(false);
      setParsedRows([]);
      setActiveTab('upload');
      void queryClient.invalidateQueries({ queryKey: ['price-adjustment-products'] });
      void queryClient.invalidateQueries({ queryKey: ['price-adjustment-brands'] });
    },
    onError: (err) => {
      alert(`Erreur d'enregistrement: ${err instanceof Error ? err.message : String(err)}`);
    },
  });

  // Product search for manual reassignment in matcher
  const searchProductQuery = useQuery({
    queryKey: ['search-products-matching', productSearchQuery],
    queryFn: () => searchProductsForMatching(productSearchQuery),
    enabled: !!searchModalRowId && productSearchQuery.length >= 2,
  });

  // Selection handlers
  const allSelectableIds = useMemo(
    () => products.filter((p) => p.suggestedRegularTtcMillimes !== null).map((p) => p.id),
    [products],
  );

  const isAllSelected = allSelectableIds.length > 0 && allSelectableIds.every((id) => selectedIds.has(id));

  const toggleSelectAll = (): void => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allSelectableIds));
    }
  };

  const selectOnlyNeedUpdate = (): void => {
    const needUpdateIds = products
      .filter((p) => (p.status === 'increase' || p.status === 'decrease') && p.suggestedRegularTtcMillimes !== null)
      .map((p) => p.id);
    setSelectedIds(new Set(needUpdateIds));
  };

  const toggleSelect = (id: number): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApplySelected = (): void => {
    if (selectedIds.size === 0) return;
    const itemsToApply = products
      .filter((p) => selectedIds.has(p.id) && p.suggestedRegularTtcMillimes !== null)
      .map((p) => ({
        productId: p.id,
        newRegularPriceTtc: p.suggestedRegularTtcMillimes!,
      }));

    if (itemsToApply.length === 0) return;

    const brandLabel = selectedBrand === 'all' ? 'Sélection' : `Marque ${selectedBrand}`;
    const dest = syncToWoo ? 'WooCommerce et en base locale' : 'base locale uniquement';
    const ok = window.confirm(
      `Confirmez-vous l'application des nouveaux prix conseillés (+${marginPct}%) pour ${itemsToApply.length} article(s) (${brandLabel}) sur ${dest} ?`,
    );

    if (ok) {
      applyMutation.mutate({ items: itemsToApply, syncToWoo });
    }
  };

  const handleApplySingle = (product: PriceAdjustmentProductItem): void => {
    if (!product.suggestedRegularTtcMillimes) return;
    const ok = window.confirm(
      `Mettre à jour "${product.name}" sur WooCommerce au prix conseillé de ${formatMillimes(product.suggestedRegularTtcMillimes)} ?`,
    );
    if (ok) {
      applyMutation.mutate({
        items: [{ productId: product.id, newRegularPriceTtc: product.suggestedRegularTtcMillimes }],
        syncToWoo: true,
      });
    }
  };

  const startInlineEdit = (p: PriceAdjustmentProductItem): void => {
    setEditingProductId(p.id);
    setInlinePriceInput(p.purchasePriceHtMillimes ? String(p.purchasePriceHtMillimes / 1000) : '');
  };

  const saveInlineEdit = (productId: number): void => {
    const val = parseFloat(inlinePriceInput.replace(',', '.'));
    if (!Number.isFinite(val) || val <= 0) {
      alert('Veuillez saisir un prix valide');
      return;
    }
    updatePriceMutation.mutate({ productId, purchasePriceHt: val });
  };

  // Export link
  const exportUrl = `/api/v1/price-adjustment/export.xlsx?brand=${encodeURIComponent(selectedBrand)}&marginPct=${marginPct}&rounding=${rounding}`;

  return (
    <div className="space-y-6 pb-20">
      {/* Top Header */}
      <PageHeader
        index="S28"
        eyebrow="Catalogue"
        title="Ajustement Prix & Marges"
        purpose="Calcul automatique et mise à jour des prix de vente WooCommerce par marque basé sur le prix d'achat et la marge conseillée (+25%)."
        meta={[
          { label: 'Module', value: 'S28' },
          { label: 'Marques actives', value: String(brandsQuery.data?.brands.length ?? '—') },
          { label: 'Couverture Prix Achat', value: summary ? `${Math.round((summary.withPurchasePrice / (summary.totalProducts || 1)) * 100)} %` : '—' },
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              onClick={() => setMatcherModalOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2 text-xs shadow-xs"
            >
              <Upload className="h-4 w-4" />
              Rapprocher Liste Fournisseur
            </Button>
            <a
              href={exportUrl}
              download
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-cream text-ink text-xs font-medium transition-colors shadow-2xs"
            >
              <Download className="h-4 w-4 text-ink-soft" />
              Exporter Excel (.xlsx)
            </a>
            <Button
              variant="ghost"
              onClick={() => {
                void queryClient.invalidateQueries({ queryKey: ['price-adjustment-products'] });
                void queryClient.invalidateQueries({ queryKey: ['price-adjustment-brands'] });
              }}
              className="text-xs"
              title="Rafraîchir"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          </div>
        }
      />

      {/* Control Panel: Brand & Margin settings */}
      <Panel className="p-4 sm:p-5 border-border bg-card shadow-xs">
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-12 gap-4 items-end">
          {/* Brand select with quick search */}
          <div className="lg:col-span-4 space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-ink-soft flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5 text-sky-600" />
                Marque du Catalogue
              </label>
              {brandsQuery.data?.brands && (
                <span className="text-[11px] text-ink-faint">
                  {brandsQuery.data.brands.length} marques
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <select
                value={selectedBrand}
                onChange={(e) => {
                  setSelectedBrand(e.target.value);
                  setExcludedSubBrands([]);
                  setPage(1);
                  setSelectedIds(new Set());
                }}
                className="flex-1 min-w-0 rounded-lg border border-border bg-paper px-3 py-2 text-xs font-medium text-ink focus:outline-none focus:ring-2 focus:ring-sky-500/30"
              >
                <option value="all">Toutes les marques (Catalogue global)</option>
                {filteredBrands.map((b) => (
                  <option key={b.brand} value={b.brand}>
                    {b.brand} ({b.totalProducts} art. · {b.withPurchasePrice} avec prix achat)
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={brandSearchInput}
                onChange={(e) => setBrandSearchInput(e.target.value)}
                placeholder="Filtrer…"
                title="Filtrer la liste des marques"
                className="w-24 rounded-lg border border-border bg-paper px-2 py-2 text-xs text-ink placeholder:text-ink-ghost focus:outline-none focus:ring-2 focus:ring-sky-500/30"
              />
            </div>
          </div>

          {/* Margin % input + quick pills */}
          <div className="lg:col-span-4 space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-ink-soft flex items-center gap-1.5">
                <Percent className="h-3.5 w-3.5 text-herb-600" />
                Marge Conseillée TTC
              </label>
              <span className="text-[11px] font-mono font-bold text-herb-700 bg-herb-50 px-1.5 py-0.5 rounded border border-herb-200">
                +{marginPct} %
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min="0"
                max="200"
                step="1"
                value={marginPct}
                onChange={(e) => setMarginPct(Math.max(0, parseInt(e.target.value || '0', 10)))}
                className="w-20 rounded-lg border border-border bg-paper px-2.5 py-1.5 text-xs font-bold text-center text-ink focus:outline-none focus:ring-2 focus:ring-herb-500/30"
              />
              <div className="flex flex-wrap gap-1 flex-1">
                {[15, 20, 25, 30, 35, 40].map((val) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setMarginPct(val)}
                    className={cn(
                      'px-2 py-1 rounded text-[11px] font-bold transition-all cursor-pointer',
                      marginPct === val
                        ? 'bg-herb-700 text-white shadow-2xs'
                        : 'bg-cream text-ink-soft hover:bg-cream/90 hover:text-ink border border-border/60',
                    )}
                  >
                    +{val}%
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Rounding Mode (xxx.xx0 rule) */}
          <div className="lg:col-span-2 space-y-1.5">
            <label className="text-xs font-semibold text-ink-soft flex items-center gap-1.5">
              <Sliders className="h-3.5 w-3.5 text-indigo-600" />
              Arrondi
            </label>
            <select
              value={rounding}
              onChange={(e) => setRounding(e.target.value as PriceRoundingStrategy)}
              className="w-full rounded-lg border border-border bg-paper px-2.5 py-2 text-xs font-medium text-ink focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
            >
              <option value="ten_millimes">10 millimes (xxx.xx0 DT)</option>
              <option value="hundred_millimes">100 millimes (xxx.x00 DT)</option>
              <option value="dinars">Dinar entier (xxx.000 DT)</option>
              <option value="ceil_dinars">Dinar supérieur</option>
              <option value="exact">Exact (fin 0 · xxx.xx0)</option>
            </select>
          </div>

          {/* Search box */}
          <div className="lg:col-span-2 space-y-1.5">
            <label className="text-xs font-semibold text-ink-soft flex items-center gap-1.5">
              <Search className="h-3.5 w-3.5 text-ink-ghost" />
              Recherche
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Nom, SKU, EAN..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-lg border border-border bg-paper pl-2.5 pr-7 py-2 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-sky-500/30"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-2 text-ink-ghost hover:text-ink"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Sub-brands & ranges checkbox pills */}
        <div className="mt-3">
          <SubBrandPills
            brand={selectedBrand}
            excludedSubBrands={excludedSubBrands}
            onChange={(ex) => {
              setExcludedSubBrands(ex);
              setPage(1);
            }}
          />
        </div>

        {/* Secondary Filter Pills */}
        <div className="mt-4 pt-3 border-t border-border/60 flex flex-wrap items-center justify-between gap-2 text-xs">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-ink-ghost font-medium mr-1 text-[11px] uppercase tracking-wider">Filtrer:</span>
            {([
              { key: 'all', label: `Tous (${summary?.totalProducts ?? 0})` },
              { key: 'need_update', label: `⚠️ À corriger (${summary?.needUpdateCount ?? 0})` },
              { key: 'increase', label: `📈 Augmentation (${summary?.increaseCount ?? 0})` },
              { key: 'decrease', label: `📉 Baisse (${summary?.decreaseCount ?? 0})` },
              { key: 'equal', label: `🟢 Aligné (${summary?.equalCount ?? 0})` },
              { key: 'missing_cost', label: `❓ Sans prix achat (${summary?.missingPurchasePrice ?? 0})` },
            ] as const).map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => {
                  setStatusFilter(f.key);
                  setPage(1);
                }}
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer border',
                  statusFilter === f.key
                    ? 'bg-sky-50 border-sky-300 text-sky-800 font-bold shadow-2xs'
                    : 'bg-card border-border text-ink-soft hover:bg-cream hover:text-ink',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Quick selection triggers */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={selectOnlyNeedUpdate}
              className="text-[11px] font-semibold text-sky-700 hover:text-sky-900 underline cursor-pointer"
            >
              Cocher uniquement ceux à mettre à jour ({summary?.needUpdateCount ?? 0})
            </button>
          </div>
        </div>
      </Panel>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-border bg-card p-4 shadow-2xs">
          <p className="text-[11px] font-medium uppercase tracking-wider text-ink-ghost">Articles Sélectionnés</p>
          <p className="mt-1 font-display text-2xl font-bold text-ink">{summary?.totalProducts ?? '—'}</p>
          <p className="mt-0.5 text-xs text-ink-soft">
            dont <span className="font-semibold text-emerald-700">{summary?.withPurchasePrice ?? 0}</span> avec Prix d'Achat
          </p>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 shadow-2xs">
          <p className="text-[11px] font-medium uppercase tracking-wider text-ink-ghost">À Corriger sur WooCommerce</p>
          <p className="mt-1 font-display text-2xl font-bold text-amber-700">{summary?.needUpdateCount ?? '—'}</p>
          <p className="mt-0.5 text-xs text-ink-soft">
            {summary?.increaseCount ?? 0} hausses · {summary?.decreaseCount ?? 0} baisses
          </p>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 shadow-2xs">
          <p className="text-[11px] font-medium uppercase tracking-wider text-ink-ghost">Marge Cible Appliquée</p>
          <p className="mt-1 font-display text-2xl font-bold text-herb-700">+{marginPct} %</p>
          <p className="mt-0.5 text-xs text-ink-soft">
            Arrondi: <span className="font-semibold">{rounding === 'dinars' ? 'Dinar entier' : rounding}</span>
          </p>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 shadow-2xs">
          <p className="text-[11px] font-medium uppercase tracking-wider text-ink-ghost">Écart Moyen Calculé</p>
          <p
            className={cn(
              'mt-1 font-display text-2xl font-bold',
              (summary?.avgDiffTnd ?? 0) > 0 ? 'text-emerald-700' : 'text-ink',
            )}
          >
            {summary?.avgDiffTnd !== undefined ? `${summary.avgDiffTnd > 0 ? '+' : ''}${summary.avgDiffTnd.toFixed(3)} DT` : '—'}
          </p>
          <p className="mt-0.5 text-xs text-ink-soft">Moyenne par article à ajuster</p>
        </div>
      </div>

      {/* Products Table */}
      <Panel className="border-border bg-card shadow-xs overflow-hidden">
        <TableShell>
          <Table>
            <THead>
              <Tr>
                <Th className="w-10 text-center">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    title="Tout sélectionner"
                    className="rounded border-border text-sky-600 focus:ring-sky-500 cursor-pointer"
                  />
                </Th>
                <Th className="min-w-[280px]">Article & Identification</Th>
                <Th className="w-32 text-right">Prix Achat HT</Th>
                <Th className="w-24 text-center">TVA</Th>
                <Th className="w-32 text-right">Achat TTC</Th>
                <Th className="w-24 text-center">Marge</Th>
                <Th className="w-36 text-right bg-sky-50/50 text-sky-900 font-bold">Prix Conseillé TTC</Th>
                <Th className="w-32 text-right">Prix Woo Actuel</Th>
                <Th className="w-36 text-center">Écart (Δ)</Th>
                <Th className="w-28 text-center">Action</Th>
              </Tr>
            </THead>
            <TBody>
              {productsQuery.isLoading ? (
                <Tr>
                  <Td colSpan={10} className="text-center py-12 text-ink-ghost">
                    Chargement des articles et calcul des marges...
                  </Td>
                </Tr>
              ) : products.length === 0 ? (
                <Tr>
                  <Td colSpan={10} className="text-center py-12 text-ink-ghost">
                    Aucun produit trouvé pour les critères sélectionnés.
                  </Td>
                </Tr>
              ) : (
                products.map((p) => {
                  const isSelected = selectedIds.has(p.id);
                  const isEditing = editingProductId === p.id;
                  const hasCost = p.purchasePriceHtMillimes !== null;

                  return (
                    <Tr
                      key={p.id}
                      className={cn(
                        'transition-colors',
                        isSelected ? 'bg-sky-50/40' : 'hover:bg-cream/40',
                      )}
                    >
                      {/* Checkbox */}
                      <Td className="text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={!p.suggestedRegularTtcMillimes}
                          onChange={() => toggleSelect(p.id)}
                          className="rounded border-border text-sky-600 focus:ring-sky-500 cursor-pointer disabled:opacity-30"
                        />
                      </Td>

                      {/* Product Name, SKU, EAN */}
                      <Td>
                        <div className="flex items-start gap-2.5">
                          {p.images[0] ? (
                            <img
                              src={p.images[0]}
                              alt=""
                              className="h-16 w-16 shrink-0 rounded-xl object-contain border border-border bg-white p-1 shadow-xs"
                            />
                          ) : (
                            <div className="h-16 w-16 shrink-0 rounded-xl border border-border bg-white grid place-items-center text-ink-ghost shadow-xs">
                              <Package className="h-7 w-7" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold text-ink line-clamp-2 leading-snug">{p.name}</p>
                            <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[11px] font-mono text-ink-soft">
                              <span className="font-bold text-sky-800 bg-sky-50 px-1 rounded border border-sky-200">
                                {p.sku}
                              </span>
                              {p.gtinCanonical && (
                                <span className="text-ink-ghost bg-paper px-1 rounded border border-border">
                                  EAN: {p.gtinCanonical}
                                </span>
                              )}
                              {p.brand && (
                                <span className="font-sans text-[10px] text-ink-ghost bg-cream px-1.5 rounded">
                                  {p.brand}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Td>

                      {/* Purchase Price HT (with inline edit) */}
                      <Td className="text-right font-mono">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-1">
                            <input
                              type="text"
                              value={inlinePriceInput}
                              onChange={(e) => setInlinePriceInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveInlineEdit(p.id);
                                if (e.key === 'Escape') setEditingProductId(null);
                              }}
                              autoFocus
                              className="w-20 rounded border border-sky-400 bg-paper px-1.5 py-0.5 text-xs text-right font-mono font-bold text-ink focus:outline-none"
                            />
                            <button
                              type="button"
                              onClick={() => saveInlineEdit(p.id)}
                              className="p-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer"
                              title="Valider"
                            >
                              <Check className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingProductId(null)}
                              className="p-1 rounded bg-card text-ink-ghost hover:text-ink cursor-pointer"
                              title="Annuler"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="group flex items-center justify-end gap-1.5">
                            {hasCost ? (
                              <span className="font-semibold text-ink">{formatMillimes(p.purchasePriceHtMillimes)}</span>
                            ) : (
                              <span className="text-[11px] font-sans text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200">
                                Non renseigné
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => startInlineEdit(p)}
                              className="opacity-0 group-hover:opacity-100 p-1 text-ink-ghost hover:text-sky-700 rounded transition-opacity cursor-pointer"
                              title="Modifier le prix d'achat HT"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                        {p.purchaseSourceKey && (
                          <p className="text-[10px] font-sans text-ink-ghost truncate mt-0.5" title={p.purchaseSourceKey}>
                            {p.purchaseSourceKey}
                          </p>
                        )}
                      </Td>

                      {/* TVA */}
                      <Td className="text-center font-mono text-xs text-ink-soft">
                        {p.purchaseVatRate} %
                      </Td>

                      {/* Cost TTC */}
                      <Td className="text-right font-mono text-xs text-ink-soft">
                        {p.costTtcMillimes ? formatMillimes(p.costTtcMillimes) : '—'}
                      </Td>

                      {/* Margin % */}
                      <Td className="text-center">
                        <span className="inline-block px-1.5 py-0.5 rounded text-[11px] font-mono font-bold text-herb-800 bg-herb-50 border border-herb-200">
                          +{marginPct}%
                        </span>
                      </Td>

                      {/* Suggested Selling Price TTC */}
                      <Td className="text-right font-mono bg-sky-50/30">
                        {p.suggestedRegularTtcMillimes ? (
                          <span className="text-sm font-bold text-sky-950">
                            {formatMillimes(p.suggestedRegularTtcMillimes)}
                          </span>
                        ) : (
                          <span className="text-ink-ghost text-xs">—</span>
                        )}
                      </Td>

                      {/* Current Woo Regular Price */}
                      <Td className="text-right font-mono text-xs text-ink">
                        {p.currentRegularPriceTtcMillimes ? (
                          formatMillimes(p.currentRegularPriceTtcMillimes)
                        ) : (
                          <span className="text-ink-ghost text-xs">Non défini</span>
                        )}
                      </Td>

                      {/* Gap / Difference */}
                      <Td className="text-center">
                        {p.diffTnd !== null ? (
                          <div className="flex flex-col items-center">
                            <span
                              className={cn(
                                'inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-bold border',
                                p.status === 'increase'
                                  ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                                  : p.status === 'decrease'
                                    ? 'bg-rose-50 text-rose-800 border-rose-300'
                                    : 'bg-card text-ink-ghost border-border',
                              )}
                            >
                              {p.status === 'increase' ? (
                                <TrendingUp className="h-3 w-3" />
                              ) : p.status === 'decrease' ? (
                                <TrendingDown className="h-3 w-3" />
                              ) : (
                                <Equal className="h-3 w-3" />
                              )}
                              {p.diffTnd > 0 ? '+' : ''}{p.diffTnd.toFixed(3)} DT
                            </span>
                            {p.diffPct !== null && p.status !== 'equal' && (
                              <span className="text-[10px] text-ink-ghost mt-0.5">
                                {p.diffPct > 0 ? '+' : ''}
                                {p.diffPct.toFixed(1)} %
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-ink-ghost text-xs">—</span>
                        )}
                      </Td>

                      {/* Action */}
                      <Td className="text-center">
                        {p.status !== 'equal' && p.suggestedRegularTtcMillimes ? (
                          <Button
                            variant="soft"
                            size="sm"
                            onClick={() => handleApplySingle(p)}
                            className="text-[11px] py-1 px-2 text-sky-700 hover:text-sky-900 border-sky-300 hover:bg-sky-50 shadow-2xs"
                          >
                            Appliquer
                          </Button>
                        ) : p.status === 'equal' ? (
                          <span className="text-[11px] font-semibold text-emerald-700 flex items-center justify-center gap-1">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Aligné
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => startInlineEdit(p)}
                            className="text-[11px] font-medium text-sky-600 hover:underline cursor-pointer"
                          >
                            + Prix achat
                          </button>
                        )}
                      </Td>
                    </Tr>
                  );
                })
              )}
            </TBody>
          </Table>
        </TableShell>

        {/* Pagination bar */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-paper/50 text-xs">
            <span className="text-ink-soft">
              Affichage {products.length} sur {pagination.totalItems} articles (Page {pagination.page} / {pagination.totalPages})
            </span>
            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Précédent
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </Panel>

      {/* Floating Batch Action Bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-card border-2 border-sky-600 shadow-2xl rounded-2xl px-5 py-3.5 flex flex-wrap items-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-200">
          <div className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-full bg-sky-600 text-white font-bold text-xs grid place-items-center shadow-xs">
              {selectedIds.size}
            </span>
            <div>
              <p className="text-xs font-bold text-ink">
                {selectedIds.size} article(s) sélectionné(s)
              </p>
              <p className="text-[11px] text-ink-soft">Marge appliquée: +{marginPct}%</p>
            </div>
          </div>

          <div className="h-6 w-px bg-border hidden sm:block" />

          {/* Sync to Woo toggle */}
          <label className="flex items-center gap-2 text-xs font-medium text-ink cursor-pointer select-none">
            <input
              type="checkbox"
              checked={syncToWoo}
              onChange={(e) => setSyncToWoo(e.target.checked)}
              className="rounded border-border text-sky-600 focus:ring-sky-500 cursor-pointer"
            />
            <span>Mettre à jour sur WooCommerce (Live)</span>
          </label>

          {/* Action button */}
          <Button
            variant="primary"
            onClick={handleApplySelected}
            disabled={applyMutation.isPending}
            className="bg-sky-600 hover:bg-sky-700 text-white font-bold text-xs px-4 py-2 shadow-xs cursor-pointer"
          >
            {applyMutation.isPending ? 'Mise à jour en cours...' : `🚀 Appliquer les prix conseillés (${selectedIds.size})`}
          </Button>

          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-ink-ghost hover:text-ink underline ml-1 cursor-pointer"
          >
            Désélectionner
          </button>
        </div>
      )}

      {/* =========================================================================
          SUPPLIER MATCHER MODAL (Generic for all brands and suppliers)
         ========================================================================= */}
      {matcherModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-card border border-border shadow-2xl rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden my-auto animate-in zoom-in-95 duration-150">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-paper">
                <div className="flex items-center gap-3">
                  <span className="h-9 w-9 rounded-lg bg-emerald-600 text-white grid place-items-center shadow-xs">
                    <FileSpreadsheet className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-display text-base font-bold text-ink">
                      Rapprochement Liste Fournisseur (Prix d'Achat HT)
                    </h3>
                    <p className="text-xs text-ink-soft">
                      Faites correspondre les prix grossiste (Excel, CSV ou copier-coller) avec votre catalogue pour renseigner automatiquement les prix d'achat HT toutes marques.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setMatcherModalOpen(false)}
                  className="p-1 rounded-lg text-ink-ghost hover:text-ink hover:bg-cream transition-colors cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {activeTab === 'upload' ? (
                <div className="space-y-6">
                  {/* Supplier Name */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-ink">Nom du Fournisseur / Source</label>
                    <input
                      type="text"
                      value={matcherSupplierName}
                      onChange={(e) => setMatcherSupplierName(e.target.value)}
                      className="w-full sm:w-80 rounded-lg border border-border bg-paper px-3 py-1.5 text-xs font-semibold text-ink focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                    />
                  </div>

                  {/* File upload input (Primary for all brands & suppliers) */}
                  <div className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-emerald-500 transition-colors bg-paper/50">
                    <Upload className="h-8 w-8 mx-auto text-emerald-600 mb-2" />
                    <p className="text-xs font-bold text-ink">Glissez votre fichier Excel (.xlsx) ou CSV fournisseur ici</p>
                    <p className="text-[11px] text-ink-soft mt-1">
                      Compatible avec toutes marques · Colonnes reconnues : Désignation/Nom, Famille/Marque, Prix HT
                    </p>
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const fd = new FormData();
                          fd.append('file', file);
                          parseFileMutation.mutate(fd);
                        }
                      }}
                      className="mt-3 block mx-auto text-xs text-ink-soft file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-emerald-600 file:text-white hover:file:bg-emerald-700 cursor-pointer"
                    />
                  </div>

                  {/* Paste text area */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-ink">Ou collez directement les lignes (Excel / Tabulé / CSV) :</label>
                    <textarea
                      rows={4}
                      value={pastedText}
                      onChange={(e) => setPastedText(e.target.value)}
                      placeholder="Désignation Produit	Marque	Prix HT	Prix Public TTC..."
                      className="w-full rounded-lg border border-border bg-paper p-3 text-xs font-mono text-ink focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                    />
                    <Button
                      variant="soft"
                      size="sm"
                      disabled={!pastedText.trim() || parseFileMutation.isPending}
                      onClick={() => parseFileMutation.mutate({ rawText: pastedText })}
                      className="text-xs font-semibold cursor-pointer"
                    >
                      {parseFileMutation.isPending ? 'Analyse...' : 'Analyser le texte collé'}
                    </Button>
                  </div>

                  <div className="relative flex py-1 items-center">
                    <div className="flex-grow border-t border-border"></div>
                    <span className="flex-shrink mx-4 text-[11px] font-medium text-ink-ghost uppercase">Optionnel : Modèle Serveur</span>
                    <div className="flex-grow border-t border-border"></div>
                  </div>

                  {/* Server File Shortcut */}
                  <div className="rounded-xl border border-border bg-card p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="space-y-0.5">
                      <p className="text-xs font-bold text-ink flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-emerald-600" />
                        Fichier modèle présent sur le serveur : "LISTE DES PRIX 2M PHARMA & PHARMA+ 2026.xlsx"
                      </p>
                      <p className="text-[11px] text-ink-soft">
                        Contient les tarifs de plusieurs marques (ISDIN, ALLIANCE, BAILLEUL, GAMARDE, GIFRER, SINCLAIR, ACM, etc.)
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => parseFileMutation.mutate({ loadServerSample: true })}
                      disabled={parseFileMutation.isPending}
                      className="text-xs font-semibold shrink-0 cursor-pointer"
                    >
                      {parseFileMutation.isPending ? 'Chargement...' : '⚡ Charger ce fichier'}
                    </Button>
                  </div>
                </div>
              ) : (
                /* REVIEW TAB */
                <div className="space-y-4">
                  {/* Summary banner */}
                  <div className="rounded-xl border border-border bg-paper p-3 flex flex-wrap items-center justify-between gap-3 text-xs">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-ink">{parsedRows.length} lignes analysées</span>
                      <span className="text-emerald-700 font-semibold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        {parsedRows.filter((r) => r.confidence === 'high').length} correspondance(s) haute(s)
                      </span>
                      <span className="text-amber-700 font-semibold bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                        {parsedRows.filter((r) => r.confidence === 'medium').length} moyenne(s)
                      </span>
                      <span className="text-rose-700 font-semibold bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                        {parsedRows.filter((r) => !r.matchedProduct).length} non trouvé(s)
                      </span>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveTab('upload')}
                      className="text-xs text-ink-ghost hover:text-ink"
                    >
                      ← Recommencer / Changer de fichier
                    </Button>
                  </div>

                  {/* Matching Rows Table */}
                  <div className="border border-border rounded-xl overflow-hidden">
                    <TableShell>
                      <Table>
                        <THead>
                          <Tr>
                            <Th className="w-10 text-center">Inclure</Th>
                            <Th className="w-1/3">Ligne Fournisseur</Th>
                            <Th className="w-24 text-right">Prix HT (DT)</Th>
                            <Th className="w-1/3">Produit Catalogue Associé</Th>
                            <Th className="w-28 text-center">Confiance</Th>
                            <Th className="w-24 text-center">Changer</Th>
                          </Tr>
                        </THead>
                        <TBody>
                          {parsedRows.map((row) => {
                            const isExcluded = excludedRowIds.has(row.rowId);
                            const matched = row.matchedProduct;

                            return (
                              <Tr
                                key={row.rowId}
                                className={cn(
                                  'text-xs transition-colors',
                                  isExcluded ? 'opacity-40 bg-card/60' : 'hover:bg-cream/40',
                                )}
                              >
                                {/* Checkbox */}
                                <Td className="text-center">
                                  <input
                                    type="checkbox"
                                    checked={!isExcluded && !!matched}
                                    disabled={!matched}
                                    onChange={() => {
                                      setExcludedRowIds((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(row.rowId)) next.delete(row.rowId);
                                        else next.add(row.rowId);
                                        return next;
                                      });
                                    }}
                                    className="rounded border-border text-emerald-600 focus:ring-emerald-500 cursor-pointer disabled:opacity-30"
                                  />
                                </Td>

                                {/* Supplier Designation & Brand */}
                                <Td>
                                  <p className="font-semibold text-ink line-clamp-1">{row.rawDesignation}</p>
                                  <div className="flex items-center gap-2 mt-0.5 text-[11px] text-ink-soft">
                                    <span className="font-bold text-ink-ghost">{row.rawBrand || 'Sans marque'}</span>
                                    <span>· Onglet: {row.sheetName}</span>
                                    {row.wholesalerSuggestedTtc && (
                                      <span className="text-herb-700 bg-herb-50 px-1 rounded font-mono">
                                        Conseillé: {row.wholesalerSuggestedTtc} DT
                                      </span>
                                    )}
                                  </div>
                                </Td>

                                {/* Price HT */}
                                <Td className="text-right font-mono font-bold text-ink">
                                  {row.priceHt.toFixed(3)} DT
                                </Td>

                                {/* Catalog Product Matched */}
                                <Td>
                                  {matched ? (
                                    <div>
                                      <p className="font-semibold text-sky-950 line-clamp-1">{matched.name}</p>
                                      <div className="flex items-center gap-2 text-[11px] font-mono text-ink-soft mt-0.5">
                                        <span className="font-bold text-sky-800">{matched.sku}</span>
                                        {matched.gtinCanonical && <span>EAN: {matched.gtinCanonical}</span>}
                                        {matched.currentPurchasePriceHt && (
                                          <span className="text-ink-ghost">
                                            Actuel Achat: {formatMillimes(matched.currentPurchasePriceHt)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-rose-600 italic">Aucune correspondance trouvée</span>
                                  )}
                                </Td>

                                {/* Confidence Score Badge */}
                                <Td className="text-center">
                                  <span
                                    className={cn(
                                      'inline-block px-2 py-0.5 rounded-full text-[11px] font-bold border',
                                      row.confidence === 'high'
                                        ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                                        : row.confidence === 'medium'
                                          ? 'bg-amber-50 text-amber-800 border-amber-300'
                                          : 'bg-rose-50 text-rose-800 border-rose-300',
                                    )}
                                  >
                                    {row.score}% · {row.confidence === 'high' ? 'Haute' : row.confidence === 'medium' ? 'Moyenne' : 'Non'}
                                  </span>
                                </Td>

                                {/* Manual Change Button */}
                                <Td className="text-center">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSearchModalRowId(row.rowId);
                                      setProductSearchQuery(row.rawDesignation.split('-')[0]?.trim() || row.rawDesignation);
                                    }}
                                    className="text-[11px] py-1 text-sky-700 hover:text-sky-900"
                                  >
                                    Modifier
                                  </Button>
                                </Td>
                              </Tr>
                            );
                          })}
                        </TBody>
                      </Table>
                    </TableShell>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-border flex items-center justify-between bg-paper">
              <span className="text-xs text-ink-soft">
                {activeTab === 'review'
                  ? `${parsedRows.filter((r) => !excludedRowIds.has(r.rowId) && !!r.matchedProduct).length} produit(s) prêt(s) à être enregistrés`
                  : 'Étape 1 sur 2 : Sélection du fichier'}
              </span>

              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => setMatcherModalOpen(false)}>
                  Fermer
                </Button>
                {activeTab === 'review' && (
                  <Button
                    variant="primary"
                    disabled={commitMatchesMutation.isPending}
                    onClick={() => {
                      const validMatches = parsedRows
                        .filter((r) => !excludedRowIds.has(r.rowId) && !!r.matchedProduct)
                        .map((r) => ({
                          productId: r.matchedProduct!.id,
                          purchasePriceHt: r.priceHt,
                          purchaseVatRate: r.vatRate,
                        }));

                      if (validMatches.length === 0) {
                        alert('Aucun produit valide à enregistrer');
                        return;
                      }

                      commitMatchesMutation.mutate({
                        supplierName: matcherSupplierName,
                        matches: validMatches,
                      });
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-xs"
                  >
                    {commitMatchesMutation.isPending
                      ? 'Enregistrement...'
                      : `Enregistrer les prix d'achat (${parsedRows.filter((r) => !excludedRowIds.has(r.rowId) && !!r.matchedProduct).length} articles)`}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Manual Search Reassignment Modal */}
      {searchModalRowId &&
        createPortal(
          <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-card border border-border shadow-2xl rounded-xl w-full max-w-xl p-5 space-y-4 my-auto animate-in zoom-in-95 duration-150">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-ink">Rechercher le produit catalogue correspondant</h4>
                <button
                  type="button"
                  onClick={() => setSearchModalRowId(null)}
                  className="text-ink-ghost hover:text-ink cursor-pointer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <input
                type="text"
                autoFocus
                value={productSearchQuery}
                onChange={(e) => setProductSearchQuery(e.target.value)}
                placeholder="Rechercher par nom, référence ou EAN..."
                className="w-full rounded-lg border border-border bg-paper p-2.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-sky-500/30"
              />

              <div className="max-h-60 overflow-y-auto space-y-1.5">
                {searchProductQuery.data?.products?.map((p: MatchedProductItem) => (
                  <div
                    key={p.id}
                    onClick={() => {
                      setParsedRows((prev) =>
                        prev.map((r) =>
                          r.rowId === searchModalRowId
                            ? {
                                ...r,
                                matchedProduct: {
                                  id: p.id,
                                  wcProductId: p.wcProductId,
                                  sku: p.sku,
                                  name: p.name,
                                  brand: p.brand,
                                  gtinCanonical: p.gtinCanonical,
                                  currentRegularPriceTtc: p.currentRegularPriceTtc,
                                  currentPurchasePriceHt: p.currentPurchasePriceHt,
                                  image: p.image,
                                },
                                confidence: 'high',
                                score: 100,
                              }
                            : r,
                        ),
                      );
                      setSearchModalRowId(null);
                    }}
                    className="p-2.5 rounded-lg border border-border bg-paper hover:bg-sky-50/50 hover:border-sky-300 cursor-pointer flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-ink line-clamp-1">{p.name}</p>
                      <p className="text-[11px] font-mono text-ink-soft">
                        {p.sku} {p.gtinCanonical ? `· EAN: ${p.gtinCanonical}` : ''} · {p.brand}
                      </p>
                    </div>
                    <span className="text-xs font-bold text-sky-700 shrink-0">Choisir →</span>
                  </div>
                ))}
                {searchProductQuery.data?.products?.length === 0 && (
                  <p className="text-xs text-ink-ghost text-center py-4">Aucun produit trouvé</p>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
